import { memo, useMemo, type CSSProperties } from "react";
import type { AfirmacionVisor, EstadoAfirmacionVisor, FragmentoVisor, MencionVisor } from "../api.ts";
import { C, NODO } from "../theme.ts";

/**
 * TextoAnotado — render de UN fragmento con resaltado combinado de menciones
 * (subrayado punteado por tipo, encontradas por búsqueda de texto porque no
 * traen offsets) y afirmaciones (fondo por estado, por offsets exactos del
 * locator). Sin anidamiento DOM: se calculan puntos de corte y se emite un
 * <span> plano por segmento, cada uno con data-frag/data-start para que el
 * lector de selección (más abajo) pueda recuperar el offset absoluto.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Color por tipo de mención / estado de afirmación
// ─────────────────────────────────────────────────────────────────────────────

const MENCION_COLOR: Record<string, string> = {
  person: NODO["Person"] ?? C.tiel,
  company: NODO["Company"] ?? C.blue,
  organization: NODO["Organization"] ?? C.oro,
  location: C.blue,
  unknown: C.muted,
};

export function colorMencionTipo(tipo: string): string {
  return MENCION_COLOR[tipo] ?? C.muted;
}

/** Fondo por estado de afirmación (proposed=ámbar, approved=verde, rejected=gris). */
export function fondoAfirmacionEstado(estado: EstadoAfirmacionVisor): string {
  if (estado === "approved") return C.greenBg;
  if (estado === "rejected") return C.grayBg;
  return C.amberBg;
}

// ─────────────────────────────────────────────────────────────────────────────
// Localización de ocurrencias
// ─────────────────────────────────────────────────────────────────────────────

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Todas las ocurrencias case-insensitive de `mentionText` en `texto`. Para
 * textos de 4+ caracteres exige límites de "palabra" aproximados (evita
 * marcar "Ana" dentro de "banana"); usa lookaround con \p{L}/\p{N} en vez de
 * \b porque \b no reconoce acentos ni eñes como caracteres de palabra.
 */
export function buscarOcurrencias(texto: string, mentionText: string): { start: number; end: number }[] {
  const recortado = mentionText.trim();
  if (recortado.length === 0) return [];
  const base = escaparRegex(recortado);
  const patron = recortado.length >= 4
    ? `(?<![\\p{L}\\p{N}])${base}(?![\\p{L}\\p{N}])`
    : base;
  let re: RegExp;
  try {
    re = new RegExp(patron, "giu");
  } catch {
    return [];
  }
  const out: { start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto)) !== null) {
    if (m[0].length === 0) {
      re.lastIndex += 1;
      continue;
    }
    out.push({ start: m.index, end: m.index + m[0].length });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmentación por puntos de corte (overlaps sin anidamiento DOM)
// ─────────────────────────────────────────────────────────────────────────────

interface SpanMencion {
  mencionId: number;
  tipo: string;
  start: number;
  end: number;
}

interface SpanAfirmacion {
  afirmacionId: string;
  estado: EstadoAfirmacionVisor;
  start: number;
  end: number;
}

interface Segmento {
  start: number;
  end: number;
  mencionIds: number[];
  colorMencion: string | null;
  afirmacionIds: string[];
  fondoAfirmacion: string | null;
}

function segmentar(longitud: number, mSpans: SpanMencion[], aSpans: SpanAfirmacion[]): Segmento[] {
  const cortes = new Set<number>([0, longitud]);
  for (const s of mSpans) {
    cortes.add(Math.max(0, Math.min(longitud, s.start)));
    cortes.add(Math.max(0, Math.min(longitud, s.end)));
  }
  for (const s of aSpans) {
    cortes.add(Math.max(0, Math.min(longitud, s.start)));
    cortes.add(Math.max(0, Math.min(longitud, s.end)));
  }
  const puntos = [...cortes].sort((a, b) => a - b);
  const segmentos: Segmento[] = [];
  for (let i = 0; i < puntos.length - 1; i++) {
    const start = puntos[i];
    const end = puntos[i + 1];
    if (start === undefined || end === undefined || start >= end) continue;
    const mCubren = mSpans.filter((s) => s.start < end && s.end > start);
    const aCubren = aSpans.filter((s) => s.start < end && s.end > start);
    const primeraMencion = mCubren[0];
    const primeraAfirmacion = aCubren[0];
    segmentos.push({
      start,
      end,
      mencionIds: mCubren.map((s) => s.mencionId),
      colorMencion: primeraMencion ? colorMencionTipo(primeraMencion.tipo) : null,
      afirmacionIds: aCubren.map((s) => s.afirmacionId),
      fondoAfirmacion: primeraAfirmacion ? fondoAfirmacionEstado(primeraAfirmacion.estado) : null,
    });
  }
  return segmentos;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente
// ─────────────────────────────────────────────────────────────────────────────

export interface FiltrosVisor {
  tiposVisibles: ReadonlySet<string>;
  estadosAfirmacionVisibles: ReadonlySet<EstadoAfirmacionVisor>;
  verRechazadas: boolean;
}

export interface ResaltadoActivo {
  tipo: "mencion" | "afirmacion";
  id: number | string;
}

function TextoAnotadoBase({
  fragmento,
  menciones,
  afirmaciones,
  filtros,
  resaltar,
  onClickMencion,
  onClickAfirmacion,
}: {
  fragmento: FragmentoVisor;
  /** Todas las menciones del documento; se filtra por ocurrencia en este fragmento. */
  menciones: MencionVisor[];
  /** Todas las afirmaciones del documento; se filtra por locator.fragment_id. */
  afirmaciones: AfirmacionVisor[];
  filtros: FiltrosVisor;
  resaltar?: ResaltadoActivo | null;
  onClickMencion: (id: number) => void;
  onClickAfirmacion: (id: string) => void;
}) {
  const texto = fragmento.texto;

  // La búsqueda regex de menciones sobre el texto es lo caro (fragmentos ×
  // menciones); se memoiza para que los renders disparados por estado ajeno
  // al contenido (selección activa, resaltado) no la repitan.
  const segmentos = useMemo(() => {
    const mSpans: SpanMencion[] = [];
    for (const m of menciones) {
      if (!filtros.tiposVisibles.has(m.mention_type)) continue;
      const rechazada = m.revision?.decision === "rejected";
      if (rechazada && !filtros.verRechazadas) continue;
      for (const oc of buscarOcurrencias(texto, m.mention_text)) {
        mSpans.push({ mencionId: m.id, tipo: m.mention_type, start: oc.start, end: oc.end });
      }
    }

    const aSpans: SpanAfirmacion[] = [];
    for (const a of afirmaciones) {
      if (!filtros.estadosAfirmacionVisibles.has(a.estado)) continue;
      const loc = a.evidence_locator;
      if (!loc) continue;
      const fragId = typeof loc["fragment_id"] === "string" ? loc["fragment_id"] : null;
      if (fragId !== fragmento.fragment_id) continue;
      const start = typeof loc["start"] === "number" ? loc["start"] : null;
      const end = typeof loc["end"] === "number" ? loc["end"] : null;
      if (start === null || end === null || end <= start) continue;
      aSpans.push({ afirmacionId: a.id, estado: a.estado, start, end });
    }

    return segmentar(texto.length, mSpans, aSpans);
  }, [texto, fragmento.fragment_id, menciones, afirmaciones, filtros]);

  return (
    <p data-frag-container={fragmento.fragment_id} style={{ margin: "0 0 12px", lineHeight: 1.65, fontSize: 14, color: C.ink }}>
      {segmentos.map((seg) => {
        const resaltada = resaltar
          ? (resaltar.tipo === "mencion" && seg.mencionIds.includes(resaltar.id as number)) ||
            (resaltar.tipo === "afirmacion" && seg.afirmacionIds.includes(resaltar.id as string))
          : false;
        // Construcción condicional (no valores `undefined` explícitos): con
        // exactOptionalPropertyTypes activo, CSSProperties no siempre admite
        // asignar undefined a una propiedad opcional.
        const estilo: CSSProperties = {
          ...(seg.fondoAfirmacion ? { background: seg.fondoAfirmacion, borderRadius: 2 } : {}),
          ...(seg.colorMencion ? { borderBottom: `2px dotted ${seg.colorMencion}` } : {}),
          ...(resaltada ? { outline: `2px solid ${C.tiel}`, outlineOffset: 1 } : {}),
          ...(seg.mencionIds.length > 0 || seg.afirmacionIds.length > 0 ? { cursor: "pointer" } : {}),
        };
        return (
          <span
            key={seg.start}
            data-frag={fragmento.fragment_id}
            data-start={seg.start}
            // Listas espaciadas (no coma) para poder usar el selector ~= al
            // hacer scroll-al-texto desde una card del panel derecho.
            data-mencion-ids={seg.mencionIds.length > 0 ? seg.mencionIds.join(" ") : undefined}
            data-afirmacion-ids={seg.afirmacionIds.length > 0 ? seg.afirmacionIds.join(" ") : undefined}
            style={estilo}
            onClick={() => {
              const afId = seg.afirmacionIds[0];
              const mId = seg.mencionIds[0];
              if (afId !== undefined) onClickAfirmacion(afId);
              else if (mId !== undefined) onClickMencion(mId);
            }}
          >
            {texto.slice(seg.start, seg.end)}
          </span>
        );
      })}
    </p>
  );
}

/** Memoizado: la página re-renderiza seguido por estado global (toolbar de
 * selección, toasts); los fragmentos solo deben recalcular cuando cambian
 * datos o filtros. Requiere props con identidad estable (useMemo/useCallback
 * en la página). */
export const TextoAnotado = memo(TextoAnotadoBase);

// ─────────────────────────────────────────────────────────────────────────────
// Lector de selección: Range → offset absoluto en el fragmento, vía data-start
// de los <span> de segmento. Vive aquí porque solo tiene sentido sobre el DOM
// que este componente produce.
// ─────────────────────────────────────────────────────────────────────────────

export interface SeleccionAnotada {
  fragmentId: string;
  start: number;
  end: number;
  texto: string;
  /** Rectángulo del Range en coordenadas de viewport, para posicionar la toolbar flotante. */
  rect: DOMRect;
}

function resolverOffset(nodo: Node, offsetEnNodo: number): { fragmentId: string; offset: number } | null {
  let el: Element | null = nodo.nodeType === Node.TEXT_NODE ? nodo.parentElement : nodo as Element;
  while (el && el.getAttribute("data-start") === null) {
    el = el.parentElement;
  }
  if (!el) return null;
  const fragmentId = el.getAttribute("data-frag");
  const base = Number(el.getAttribute("data-start"));
  if (fragmentId === null || !Number.isFinite(base)) return null;
  return { fragmentId, offset: base + offsetEnNodo };
}

/**
 * Lee la selección activa del navegador. Devuelve `null` si no hay selección
 * útil, `"cruzada"` si el inicio y el fin caen en fragmentos distintos (la UI
 * debe pedir seleccionar dentro de un solo párrafo).
 */
export function leerSeleccionAnotada(): SeleccionAnotada | "cruzada" | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const texto = range.toString();
  if (texto.trim().length === 0) return null;
  const inicio = resolverOffset(range.startContainer, range.startOffset);
  const fin = resolverOffset(range.endContainer, range.endOffset);
  if (!inicio || !fin) return null;
  if (inicio.fragmentId !== fin.fragmentId) return "cruzada";
  const start = Math.min(inicio.offset, fin.offset);
  const end = Math.max(inicio.offset, fin.offset);
  if (end <= start) return null;
  return { fragmentId: inicio.fragmentId, start, end, texto, rect: range.getBoundingClientRect() };
}
